import {
  Box,
  Divider,
  InputAdornment,
  Typography,
  Grid,
  FormControlLabel,
} from '@mui/material';
import React, {useState, useEffect, useContext, useRef} from 'react';
import {AurTextField} from '../aur-text-field/AurTextField';
import {t} from 'i18next';
import {AurIpAddress} from '../aur-ip-address/AurIpAddress';
import {AurSwitch} from '../aur-switch/AurSwitch';
import AurButton from '../aur-button/AurButton';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {
  ITeamMemberInfoFormData,
  TeamMemberStatus,
} from '../../pages/user-management/models';
import {arraysEqual} from '../../../utils/utils';
import AurIcon, {IconColors, IconNames} from '../aur-icon/AurIcon';
import AurCheckbox from '../aur-checkbox/AurCheckbox';
import AurDialog from '../aur-dialog/AurDialog';
import {
  createUserRole,
  deleteUserRole,
} from '../../../api/ClientManagementService';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {AuthorizationContext} from '../../../contexts/authorizationContext/AuthorizationContext';
import {Permissions, Resources} from '../../../utils/authorization';
import {setShowModal} from '../../../redux/slices/unSaveChangesModalSlice';
import {
  removeUserRole,
  upsertUserRole,
} from '../../../redux/slices/userRoleSlice';
import {useNavigate} from 'react-router-dom';
import AurPopup from '../aur-popup/AurPopup';
import {UserRoleIds} from '../../../types/user-management/userManagementTypes';
import {UserRoleCode} from '../../../types/user-role-codes/roleCodes';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {setAnalyticsInfo} from '../../../redux/slices/teamMemberSlice';

// Define the type of props the component will receive
interface Props {
  onCancelClick: () => void; // Function to handle cancel action
  userFirstName: string; // User's first name
  userLastName: string; // User's last name
  isAdmin: boolean; // Boolean to check if the user is an admin
  onUpdateUserDetails: (teamMemberInfo: ITeamMemberInfoFormData) => void; // Function to handle submit action
  onSysAdminRoleChanged?: (change: 'promoted' | 'demoted') => void;
  isSpacesNotAllowed?: boolean; // Boolean to check if the Spaces are allowed or not
}

// Define the structure of switch label data
interface SwitchLableData {
  [key: string]: boolean;
}

// Define props for SwitchWithLabel component
interface SwitchLableProps {
  id: string;
  label: string; // The label for the switch
  state: boolean; // The initial state of the switch
  disabled?: boolean;
}

// Main functional component
const AurEditDetails: React.FC<Props> = (props: Props) => {
  // Destructure props
  const {
    onCancelClick,
    userFirstName,
    userLastName,
    isAdmin,
    onUpdateUserDetails,
    onSysAdminRoleChanged,
    isSpacesNotAllowed = false,
  } = props;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const showModal = useSelector(
    (state: RootState) => state.unsavedChangesModal.showModal
  );
  // Handler for changes in the first name input field
  const {teamMemberInfo} = useSelector((state: RootState) => state.teamMember);
  const userRoleInfo = useSelector((state: RootState) => state.userRole);
  const [isValid, setValid] = useState<boolean>(false);
  const [isLastNameValid, setLastNameValid] = useState<boolean>(false);
  const [errorTextFirstName, setErrorTextFirstName] = useState<string>('');
  const [errorTextLastName, setErrorTextLastName] = useState<string>('');
  const [isSysAdmin, setIsSysAdmin] = useState(false);
  const [hasSysAdminChanged, setHasSysAdminChanged] = useState(false);
  const [showSysAdminModal, setShowSysAdminModal] = useState(false);
  const [isValueChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {showToast} = useToast();
  const initiallySysAdmin = useRef<boolean>(false);
  const isSubmittingRef = useRef(false);
  const {trackGAEvent} = useAnalytics();

  const loggedInUserEmail = useSelector(
    (state: RootState) => state.user.user?.email
  );

  const auth = useContext(AuthorizationContext);

  useEffect(() => {
    const hasSysAdminRole = userRoleInfo.userRoles.some(
      (x) => x.role.roleId === UserRoleIds.SystemAdmin
    );
    setIsSysAdmin(hasSysAdminRole);
    initiallySysAdmin.current = hasSysAdminRole;
  }, [userRoleInfo.userRoles]);

  const handleFirstNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFirstName(value);
    if (/^[A-Za-z0-9-'\s-]+$/.test(value.replace(/\s+/g, ''))) {
      setValid(true);
      setErrorTextFirstName('');
    } else {
      setValid(false);
      setErrorTextFirstName(t('common.userProfile.nameInvalid'));
    }
  };

  // Handler for changes in the last name input field
  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLastName(value);
    if (/^[A-Za-z0-9-'\s-]+$/.test(value.replace(/\s+/g, ''))) {
      setLastNameValid(true);
      setErrorTextLastName('');
    } else {
      setLastNameValid(false);
      setErrorTextLastName(t('common.userProfile.nameInvalid'));
    }
  };

  const handleSysAdminChange = (event: any) => {
    if (event.target.checked) {
      setIsSysAdmin(true);
    } else {
      setIsSysAdmin(false);
    }
    setShowSysAdminModal(true);
  };

  // Array of switch labels and their initial states
  const switchLabelData: SwitchLableData[] = [
    {Active: false},
    {Billable: true},
    {'Limit IP Access': false},
  ];

  const [firstName, setFirstName] = useState<string>(teamMemberInfo.firstName);
  const [lastName, setLastName] = useState<string>(teamMemberInfo.lastName);
  const [ipAddressList, setipAddressList] = useState<string[]>(
    teamMemberInfo.ipAddressList
  );

  useEffect(() => {
    if (showModal) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
        subPage: {
          subpage_name: t('gaTracking.global.unsavedChanges'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  }, [showModal]);

  useEffect(() => {
    dispatch(setShowModal(false));
    isValueChanged
      ? localStorage.setItem('currentPath', window.location.pathname)
      : localStorage.setItem('currentPath', '');

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handlePopState = () => {
      // Handle back button (popstate event)
      const path = localStorage.getItem('currentPath');
      if (!path) navigate('/users');
      dispatch(setShowModal(true)); // Show custom modal
      window.history.pushState(null, '', window.location.pathname); // Prevent back navigation
    };

    // Add initial state to prevent back button navigation
    window.history.pushState(null, '', window.location.pathname);

    // Add event listeners for beforeunload and popstate
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    // Cleanup event listeners when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const isDataChanged = () => {
    // Check if the user's data has changed
    const isChanged =
      firstName !== userFirstName ||
      lastName !== userLastName ||
      !arraysEqual(ipAddressList, teamMemberInfo.ipAddressList) ||
      isActive !== teamMemberInfo.isActive ||
      isBillable !== teamMemberInfo.isBillable ||
      isLimitIp !== teamMemberInfo.isLimitIp ||
      (isLimitIp !== teamMemberInfo.isLimitIp &&
        isLimitIp &&
        ipAddressList.length > 0) ||
      isSysAdmin !== initiallySysAdmin.current;

    isChanged
      ? localStorage.setItem('currentPath', window.location.pathname)
      : localStorage?.setItem('currentPath', '');
    return isChanged;
  };

  const handlePopupCancel = () => {
    dispatch(setShowModal(false));
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.stayHere'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.global.stay'),
      },
      subPage: {
        subpage_name: t('gaTracking.global.unsavedChanges'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const handlePopupConfirm = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.yesLeaveIt'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.global.leave'),
      },
      subPage: {
        subpage_name: t('gaTracking.global.unsavedChanges'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    const link: string = localStorage?.getItem('currentLink') || '';
    navigate(link, {state: {source: 'nav-panel'}});
    localStorage?.setItem('currentPath', '');
    dispatch(setShowModal(false));
  };

  const convertString = (str: string) => {
    // Check if the string contains spaces
    if (str.includes(' ')) {
      return str
        .split(' ')
        .map((word, index) => {
          if (index === 0) return word.toLowerCase(); // First word to lowercase
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join('');
    } else {
      return str.toLowerCase();
    }
  };
  const isDataValid = () => {
    return (
      /^[A-Za-z0-9-'\s-]+$/.test(firstName.replace(/\s+/g, '')) &&
      /^[A-Za-z0-9-'\s-]+$/.test(lastName.replace(/\s+/g, ''))
    );
  };
  const onIpUpdate = (ipAddressList: string[]) => {
    setipAddressList(ipAddressList);
  };
  // State variable for toggle  status
  const [isActive, setIsActive] = useState<boolean>(teamMemberInfo.isActive);
  const [isBillable, setIsBillable] = useState<boolean>(
    teamMemberInfo.isBillable
  );
  const [isLimitIp, setIsLimitIp] = useState<boolean>(teamMemberInfo.isLimitIp);

  // Component for rendering a switch with a label
  const SwitchWithLabel = ({id, label, state}: SwitchLableProps) => {
    // Toggle handler
    const onChangeToggle = () => {
      switch (id) {
        case 'Active':
          setIsActive((prev) => !prev);
          break;
        case 'Billable':
          setIsBillable((prev) => !prev);
          break;
        case 'Limit IP Access':
          setIsLimitIp((prev) => !prev);
          break;
      }
    };

    const showInvitedBox =
      teamMemberInfo?.status === TeamMemberStatus.invited &&
      id === TeamMemberStatus.active;

    return (
      <div className="switch-label-main-div">
        <AurSwitch
          checked={state}
          onChange={onChangeToggle}
          dataTestId={id}
          ariaLabel={label}
        />
        <Typography className="typo-text body2_size mist-color-100">
          {label}
        </Typography>
        {showInvitedBox && (
          <div className="invited-box">
            <img src="/assests/images/mail.svg" alt="icon" />
            <Typography className="typo-text body2_size invited-text">
              {t('common.editDetails.invited')}
            </Typography>
          </div>
        )}
      </div>
    );
  };

  const getSysAdminModalTitle = (): string => {
    if (isSysAdmin) return t('common.editDetails.sysAdminAccessRightsWarning');
    else return t('common.editDetails.removeSysAdminAccessRightsWarning');
  };

  const getSysAdminModalContent = (): string => {
    if (isSysAdmin) return t('common.editDetails.giveTeamMemberSysAdminAccess');
    else return t('common.editDetails.removeTeamMemberSysAdminAccess');
  };

  const onSysAdminOkay = async () => {
    setHasSysAdminChanged(true);
    setShowSysAdminModal(false);
  };

  const onSysAdminCancel = () => {
    if (isSysAdmin) setIsSysAdmin(false);
    else setIsSysAdmin(true);
    setHasSysAdminChanged(false);
    setShowSysAdminModal(false);
  };

  const onSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    let isSuccess = false;

    try {
      if (hasSysAdminChanged) {
        if (isSysAdmin) {
          const data = {roleId: UserRoleIds.SystemAdmin, clientId: null};
          const savedUserRole = await createUserRole(
            teamMemberInfo.userId as string,
            data
          );
          dispatch(upsertUserRole(savedUserRole));
          void onSysAdminRoleChanged?.('promoted');
        } else {
          const sysAdminRole = userRoleInfo.userRoles.find(
            (x) =>
              x.role.roleId === UserRoleIds.SystemAdmin ||
              x.role?.roleCode === UserRoleCode.systemAdminUserRole
          );
          if (sysAdminRole) {
            const {userRoleId} = await deleteUserRole(
              teamMemberInfo.userId as string,
              sysAdminRole.userRoleId as string,
              {clientId: null}
            );
            dispatch(removeUserRole(userRoleId));
            void onSysAdminRoleChanged?.('demoted');
          } else {
            throw new Error('System Admin role not found on delete');
          }
        }
      }

      onUpdateUserDetails({
        userId: teamMemberInfo.userId,
        firstName,
        lastName,
        email: teamMemberInfo.email,
        isActive,
        isBillable,
        isLimitIp,
        ipAddressList,
        status: teamMemberInfo?.status,
      });

      showToast(
        t('common.editDetails.success'),
        'success',
        undefined,
        'center'
      );
      setTimeout(() => {
        localStorage?.setItem('currentPath', '');
      }, 500);
      setHasSysAdminChanged(false);
      isSuccess = true;
    } catch (e) {
      console.error(e);
      showToast(t('common.editDetails.error'), 'error', undefined, 'center');
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      if (isSuccess) dispatch(setShowModal(false));
    }
  };
  // Prevent entering spaces
  const handleKeyDown = (event: any) => {
    if (event.key === ' ' && isSpacesNotAllowed) {
      // Prevent space from being entered
      event.preventDefault();
    }
  };
  return (
    <>
      <Box className="edit-details-form">
        <Typography
          className="typo-text headline2_size mist-color-100"
          component="h1"
        >
          {t('common.editDetails.editTeamMember')}
        </Typography>
        <div className="edit-form-main-div">
          <div className="edit-details-fields">
            <Typography className="typo-text subhead3_size mist-color-500">
              {t('common.editDetails.teamMemberInfo')}
            </Typography>
            <AurTextField
              helperText={isValid ? '' : errorTextFirstName}
              required
              label={t('common.editDetails.firstNameLabel')}
              value={firstName}
              onKeyDown={handleKeyDown} // Listen for the key down event
              onChange={handleFirstNameChange}
              variant="filled"
              className="aur-textfield custom-text-field textfield"
              error={Boolean(errorTextFirstName)}
              ariaLabel={t('common.editDetails.firstNameLabel')}
              InputProps={{
                disableUnderline: true,
                endAdornment: errorTextFirstName ? (
                  <InputAdornment position="end">
                    <AurIcon
                      icon={IconNames.Error}
                      color={IconColors.Critical}
                    />
                  </InputAdornment>
                ) : null,
                className: errorTextFirstName && 'inputError',
              }}
              data-testid="editDetailsFirstName"
            />
            <AurTextField
              helperText={isLastNameValid ? '' : errorTextLastName}
              required
              label={t('common.editDetails.lastNameLabel')}
              value={lastName}
              onKeyDown={handleKeyDown} // Listen for the key down event
              onChange={handleLastNameChange}
              variant="filled"
              ariaLabel={t('common.editDetails.lastNameLabel')}
              className="aur-textfield custom-text-field textfield"
              error={Boolean(errorTextLastName)}
              InputProps={{
                disableUnderline: true,
                endAdornment: errorTextLastName ? (
                  <InputAdornment position="end">
                    <AurIcon
                      icon={IconNames.Error}
                      color={IconColors.Critical}
                    />
                  </InputAdornment>
                ) : null,
                className: errorTextLastName && 'inputError',
              }}
              data-testid="editDetailsLastName"
            />
            <AurTextField
              required={isAdmin}
              label={t('common.editDetails.emailLabel')}
              value={teamMemberInfo.email}
              variant="filled"
              className="textfield"
              disabled // Disable input as email cannot be changed
              helperText={!isAdmin ? t('common.editDetails.emailHelpText') : ''}
              ariaLabel={t('common.editDetails.emailLabel')}
              data-testid="editDetailsEmail"
            />
            {auth.can(Permissions.Add, Resources.AdminUsers) ? (
              <div className="left-align-center-y">
                <FormControlLabel
                  control={<AurCheckbox />}
                  checked={isSysAdmin}
                  disabled={teamMemberInfo.email === loggedInUserEmail}
                  className="sys-admin-checkbox"
                  onChange={(event, checked) => {
                    handleSysAdminChange(event);
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    checked = isSysAdmin;
                    if (isSysAdmin) {
                      dispatch(
                        setAnalyticsInfo({
                          userRole: t('gaTracking.users.sysAdmin'),
                          numberOfProject: '',
                        })
                      );
                    }
                  }}
                  label={
                    <Typography className="mist-color-500 sys-admin-label">
                      {t('addEditTeamMember.form.sysAdmin')}
                    </Typography>
                  }
                  data-testid="sysAdminCheck"
                />
              </div>
            ) : (
              <></>
            )}
            {isSysAdmin && hasSysAdminChanged ? (
              <Grid item sm={8} md={8} className="form-content">
                <Typography className="admin-info-hidden-note-text">
                  {' '}
                  {t('addEditTeamMember.form.sysAdminUpgradeAccess')}
                </Typography>
              </Grid>
            ) : (
              <></>
            )}
          </div>
          {isAdmin && (
            <div className="edit-switch-ip-main-div">
              <Typography className="typo-text subhead3_size mist-color-400">
                {t('common.editDetails.setUserDesignations')}
              </Typography>
              <div className="edit-switch-ip-sub-div">
                {switchLabelData.map((data, index) => {
                  const [label] = Object.entries(data)[0];
                  return (
                    <>
                      <SwitchWithLabel
                        id={label}
                        label={t(
                          `addEditTeamMember.form.${convertString(label)}`
                        )}
                        state={
                          label === 'Active'
                            ? isActive
                            : label === 'Billable'
                              ? isBillable
                              : isLimitIp
                        }
                      />
                      {index < switchLabelData.length - 1 ? <Divider /> : ''}
                    </>
                  );
                })}
              </div>
              {isLimitIp && (
                <AurIpAddress
                  ipAdressList={ipAddressList}
                  onIpListUpdate={onIpUpdate}
                />
              )}
            </div>
          )}
        </div>
        <div className="edit-button-div">
          <AurButton
            type="secondary"
            label={t('buttons.cancel')}
            onClick={onCancelClick}
            classNames="button"
            dataTestId="editDetailsCancelButton"
          />
          <AurButton
            disabled={isSubmitting || !isDataChanged() || !isDataValid()}
            type="continue"
            label={t('buttons.saveChanges')}
            onClick={() => onSubmit()}
            classNames="button"
            dataTestId="editDetailsSaveButton"
          />
        </div>
      </Box>
      <AurPopup
        aria-label={t(`UserProfile.unsavedChangePopup.title`)}
        aria-description={t(`UserProfile.unsavedChangePopup.heading`)}
        className="inactivity-popup-modal"
        open={showModal}
        onOk={handlePopupConfirm}
        onCancel={handlePopupCancel}
        popupTitle={t(`UserProfile.unsavedChangePopup.title`)}
        popupHeading={t(`UserProfile.unsavedChangePopup.heading`)}
        showPopupHeading={true}
        showPopupTitle={true}
        showCloseIcon={false}
        popupContent={t(`UserProfile.unsavedChangePopup.content`)}
        labelActionCancel={t(`UserProfile.unsavedChangePopup.buttonStay`)}
        labelActionOk={t(`UserProfile.unsavedChangePopup.buttonYes`)}
      />
      <AurDialog
        dialogTitle={t('common.justToConfirm')}
        popupTitle={getSysAdminModalTitle()}
        popupContent={getSysAdminModalContent()}
        labelActionOk={t('common.iAmSure')}
        labelActionCancel="Cancel"
        onOk={onSysAdminOkay}
        onCancel={onSysAdminCancel}
        showDialog={showSysAdminModal}
        buttonStyle="sideBySide"
      />
    </>
  );
};

export default AurEditDetails;
